import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, MapPin, Star, CreditCard, Check, FileText, Ban, RotateCcw, Pencil, Save, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { fmt, euro } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { Logo } from "../../components/Logo";

export default function AdminMemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const load = () => api.get(`/admin/user/${id}`).then(({ data }) => { setD(data); setName(data.user?.name || ""); }).catch(() => toast.error("Introuvable"));
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="min-h-screen bg-ink py-20 text-center text-slate-500">{t("loading")}</div>;
  const u = d.user;
  const verify = async () => { await api.post(`/admin/users/${u.id}/verify`); toast.success(t("verified")); load(); };
  const toggleSuspend = async () => {
    try { const { data } = await api.post(`/admin/users/${u.id}/suspend`); toast.success(data.suspended ? t("suspended") : t("activeLabel")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try { await api.patch(`/admin/users/${u.id}`, { name }); toast.success(t("saveChanges")); setEditing(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="sticky top-0 z-40 glass border-b border-cardline px-4 py-3">
        <div className="mx-auto flex max-w-4xl items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="member-back-btn" className="grid h-9 w-9 place-items-center rounded-full border border-cardline bg-ink2/60 text-slate-300"><ArrowLeft size={18} /></button>
          <Logo size={26} />
          <span className="text-sm font-semibold text-slate-400">{t("memberProfile")}</span>
        </div>
      </header>
      <main className="mx-auto max-w-4xl space-y-5 px-4 py-6" data-testid="admin-member-detail">
        <div className="relative overflow-hidden rounded-3xl border border-cardline">
          {u.banner && <img src={u.banner} alt="" className="h-32 w-full object-cover opacity-70" />}
          <div className="flex flex-wrap items-center gap-4 p-5">
            <img src={u.avatar} alt="" className="h-16 w-16 rounded-full border-2 border-card2 object-cover" />
            <div className="min-w-0 flex-1">
              {editing ? (
                <div className="flex items-center gap-2">
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="member-name-input"
                    className="w-full max-w-xs rounded-xl border border-cardline bg-ink2/60 px-3 py-2 text-sm outline-none focus:border-gold" />
                  <button onClick={saveEdit} data-testid="member-save-btn" className="grid h-9 w-9 place-items-center rounded-lg bg-gold text-ink"><Save size={16} /></button>
                  <button onClick={() => { setEditing(false); setName(u.name); }} className="grid h-9 w-9 place-items-center rounded-lg border border-cardline text-slate-300"><X size={16} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 font-heading text-xl font-extrabold">
                  {u.name} {u.verified && <BadgeCheck size={18} className="text-gold" />}
                  {u.suspended && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-400">{t("suspended")}</span>}
                </div>
              )}
              <div className="text-sm text-slate-400">{u.email} · {u.role}</div>
              {u.location && <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><MapPin size={12} /> {u.location}</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              {!u.verified && <button onClick={verify} data-testid="member-verify-btn" className="flex items-center gap-1 rounded-lg bg-emerald-500/20 px-3 py-2 text-sm font-semibold text-emerald-400"><Check size={15} /> {t("verify")}</button>}
              {!editing && <button onClick={() => setEditing(true)} data-testid="member-edit-btn" className="flex items-center gap-1 rounded-lg border border-cardline px-3 py-2 text-sm font-semibold text-slate-300 hover:border-gold hover:text-gold"><Pencil size={15} /> {t("editMember")}</button>}
              <button onClick={toggleSuspend} data-testid="member-suspend-btn"
                className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold ${u.suspended ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                {u.suspended ? <><RotateCcw size={15} /> {t("unsuspend")}</> : <><Ban size={15} /> {t("suspend")}</>}
              </button>
            </div>
          </div>
        </div>

        {u.role === "creator" && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Abonnés", fmt(u.followers || 0)], ["Vues", fmt(u.avg_views || 0)], ["Engag.", `${u.engagement_rate || 0}%`], ["Note", `${u.rating || 0}/5`]].map(([l, v]) => (
              <div key={l} className="rounded-2xl border border-cardline bg-card2/60 p-3 text-center"><div className="font-heading text-lg font-extrabold">{v}</div><div className="text-[11px] text-slate-400">{l}</div></div>
            ))}
          </div>
        )}
        {u.role === "creator" && (
          <div className="flex flex-wrap gap-3 text-sm">
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-semibold ${u.subscription_active ? "bg-emerald-400/10 text-emerald-400" : "bg-ink2/70 text-slate-400"}`}><Star size={12} /> {u.subscription_active ? t("subscriptionActive") : "—"}</span>
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-semibold ${u.connect_ready ? "bg-sky-400/10 text-sky-300" : "bg-ink2/70 text-slate-400"}`}><CreditCard size={12} /> {u.connect_ready ? t("payoutsActive") : "Stripe —"}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-ink2/70 px-3 py-1 text-slate-300"><FileText size={12} /> {d.deliverables || 0} {t("deliverables")}</span>
          </div>
        )}

        {u.role === "commerce" && d.campaigns && (
          <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
            <h3 className="mb-2 font-heading text-sm font-semibold">{t("campaignsCount")} ({d.campaigns.length})</h3>
            <div className="space-y-1.5">{d.campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg bg-ink2/50 px-3 py-2 text-sm"><span>{c.title}</span><span className="text-gold">{euro(c.budget)}</span></div>
            ))}</div>
          </div>
        )}

        <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
          <h3 className="mb-2 font-heading text-sm font-semibold">{t("navBilling")}</h3>
          {(!d.transactions || d.transactions.length === 0) && <p className="py-4 text-center text-sm text-slate-500">{t("noData")}</p>}
          <div className="space-y-1.5">{(d.transactions || []).map((x, i) => (
            <button key={i} onClick={() => navigate(`/admin/invoice/${x.session_id}`)} data-testid={`member-tx-${i}`}
              className="flex w-full items-center justify-between rounded-lg bg-ink2/50 px-3 py-2 text-left text-sm hover:bg-ink2">
              <span className="text-slate-300">{(x.created_at || "").slice(0, 10)} · {x.type === "subscription" ? t("navSubscriptions") : t("navBilling")}</span>
              <span className="font-semibold text-gold">{euro(x.amount)} · {x.payment_status}</span>
            </button>
          ))}</div>
        </div>
      </main>
    </div>
  );
}
